import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import {
  MatButtonToggle,
  MatButtonToggleGroup,
} from '@angular/material/button-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatMenu, MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faCircle } from '@fortawesome/free-solid-svg-icons';
import { assert, isDefined } from '@sama/js-core';
import { DirectiveSuperclass } from '@sama/ng-core';
import { BehaviorSubject, map, Subscription } from 'rxjs';
import { CrossTeamSharedModule } from '../../../../../../../cross-team-shared/cross-team-shared.module';
import { feedbackDialogService } from '../../../../../../../outputs/annotation/annotation-shared/annotation-providers';
import { CvFeedbackPanelComponent } from '../../../../../../feedback/views/cv-feedback-panel/cv-feedback-panel.component';
import { TaskWorkspaceStore } from '../../../../../../h3ml/state/task-workspace-store';
import { FeedbackListService } from '../../../../../../shared/feedback/feedback-list.service';
import { FusionManagerFactory } from '../../../fusion/fusion-manager-factory.service';

@Component({
  selector: 'h3-show-feedback-button',
  templateUrl: './show-feedback-button.component.html',
  styleUrls: ['./show-feedback-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CvFeedbackPanelComponent,
    MatBadgeModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    CommonModule,
    CrossTeamSharedModule,
    MatButtonToggleGroup,
    MatButtonToggle,
    FaIconComponent,
  ],
})
export class ShowFeedbackButtonComponent
  extends DirectiveSuperclass
  implements OnInit, OnDestroy
{
  @ViewChild(MatMenuTrigger) menuTrigger?: MatMenuTrigger;
  @ViewChild(MatMenu) menu?: MatMenu;

  faCircle = faCircle;
  isDisabled$ = new BehaviorSubject(false);
  shouldOpenFeedbackPopover$ = this.store('feedbackPopover')('target').$.pipe(
    map(isDefined),
  );
  injector$ = this.store('selectedOutputName').$.pipe(
    map(() => this.getInjector()),
  );

  private fusionManagerFactory = inject(FusionManagerFactory);
  private menuOpenSubscription?: Subscription;
  private feedbackListService = inject(FeedbackListService);
  private destroyRef = inject(DestroyRef);

  constructor(
    private store: TaskWorkspaceStore,
    injector: Injector,
  ) {
    super(injector);
    this.subscribeTo(
      this.shouldOpenFeedbackPopover$,
      (shouldOpenFeedbackPopover) => {
        if (!shouldOpenFeedbackPopover) {
          this.menuTrigger?.closeMenu();
        } else {
          // when not in split view, we need to wait for the button to be
          // rendered first when switching outputs so the menu's position
          // can be calculated correctly
          const timeout = setTimeout(() => {
            this.menuTrigger?.openMenu();
            window.dispatchEvent(new Event('resize'));
          });
          this.destroyRef.onDestroy(() => clearTimeout(timeout));
        }
      },
    );
  }

  ngOnInit() {
    const { feedbackType } = this.store.state();
    feedbackType === 'client' && this.subscribeToClientFeedbackDisabled();
    feedbackType === 'shape' && this.subscribeToShapeFeedbackDisabled();
  }

  ngOnDestroy() {
    this.menuOpenSubscription?.unsubscribe();
  }

  onPopoverOpen() {
    const tigger = this.menuTrigger;
    if (this.menuOpenSubscription || !tigger) {
      return;
    }
    this.menuOpenSubscription = tigger.menuOpened.subscribe(() => {
      this.subscribeTo(tigger.menuClosed.asObservable(), () => {
        this.clearFeedbackMarkers();
        this.feedbackListService.closeFeedbackList();
      });
    });
  }

  onPopoverClose() {
    this.clearFeedbackMarkers();
    this.feedbackListService.closeFeedbackList();
  }

  toggleFeedbackList() {
    if (this.feedbackListService.isFeedbackListOpen()) {
      this.feedbackListService.closeFeedbackList();
    } else {
      this.feedbackListService.openFeedbackList('toggle');
    }
  }

  private subscribeToShapeFeedbackDisabled() {
    this.subscribeTo(this.store('shapeFeedback').$, (feedbackList) => {
      this.isDisabled$.next(!feedbackList.length);
    });
  }

  private subscribeToClientFeedbackDisabled() {
    this.subscribeTo(this.store('clientFeedback').$, (issueList) => {
      this.isDisabled$.next(!issueList.length);
    });
  }

  private clearFeedbackMarkers() {
    this.getInjector().get(feedbackDialogService).clearFeedbackMarkers();
  }

  private getInjector() {
    const activeOutput = this.store.getSelectedOutput();
    const minion = this.fusionManagerFactory
      .getManager(activeOutput.id)
      .getMinion(activeOutput.id);
    assert(minion, 'minion should always be defined');
    return minion.injector;
  }
}
